import {ChangeDetectionStrategy, Component, computed, effect, inject} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';

import {LegalConsentModal} from 'frontend/components/legal_consent_modal/legal_consent_modal';
import {ICON_LIBRARY} from 'frontend/platform/asset_icon/asset_icon.types';
import {ErrorsService} from 'frontend/services/errors_service';
import {IconRegistry} from 'frontend/services/icon_registry';
import {LegalConsentService} from 'frontend/services/legal_consent_service';
import {RefreshTokenService} from 'frontend/services/refresh_token_service';
import {COLORS} from 'frontend/utils/color_palettes';
import {
  capacitorBankSVG,
  energyStorageSVG,
  feederSVG,
  generatorSVG,
  maxCongestionProbabilitySVG,
  modelAssetsSVG,
  openedSwitchSVG,
  reactorsSVG,
  substationSVG,
  towerSVG,
  transformerSVG,
  transmissionLineSVG,
} from 'frontend/utils/icons';
import {
  GENERATOR_TYPE_ICONS,
  GENERATOR_UNIT_LEGEND_ICON_NAME,
  TRANSMISSION_LINES_ICONS,
} from 'frontend/utils/map/shape_factories/google_maps';
import {ElectricalTypesIcon} from 'frontend/utils/model/model_analytics';

const GPT_ICONS = {
  'model-assets-header': modelAssetsSVG(),
  [GENERATOR_UNIT_LEGEND_ICON_NAME]: generatorSVG({strokeColor: COLORS.NewNeutral.get(10)}),
  [ElectricalTypesIcon.GENERATOR]: generatorSVG({}),
  ...GENERATOR_TYPE_ICONS,
  ...Object.fromEntries(TRANSMISSION_LINES_ICONS),
  [ElectricalTypesIcon.TOWERS]: towerSVG(),
  [ElectricalTypesIcon.TRANSMISSION_LINE]: transmissionLineSVG(),
  [ElectricalTypesIcon.SUBSTATION]: substationSVG(),
  [ElectricalTypesIcon.TRANSFORMER]: transformerSVG(),
  [ElectricalTypesIcon.CAPACITOR_BANKS]: capacitorBankSVG(),
  [ElectricalTypesIcon.REACTOR]: reactorsSVG(),
  [ElectricalTypesIcon.ENERGY_STORAGE]: energyStorageSVG(),
  [ElectricalTypesIcon.FEEDER]: feederSVG(),
  [ElectricalTypesIcon.SWITCH]: openedSwitchSVG(),
  'max-congestion-probability': maxCongestionProbabilitySVG(),
};

/**
 * The root component of the app.
 */
@Component({
  selector: 'tap-app',
  templateUrl: './app.ng.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class App {
  private readonly errorsService = inject(ErrorsService);
  private readonly refreshTokenService = inject(RefreshTokenService);
  private readonly iconRegistry = inject(IconRegistry);
  private readonly legalConsentService = inject(LegalConsentService);

  private dialogRef: MatDialogRef<LegalConsentModal> | null = null;

  readonly hasGlobalError = computed(() => this.errorsService.criticalError() !== null);
  readonly consentRequired = this.legalConsentService.consentRequired;

  constructor() {
    this.iconRegistry.registerSVGs(GPT_ICONS);
    this.iconRegistry.registerAssetIconLibrary(ICON_LIBRARY);

    this.refreshTokenService.startTokenRefresh();

    effect(() => {
      if (this.consentRequired()) {
        if (this.dialogRef === null) {
          this.dialogRef = this.legalConsentService.openConsentModal(false);
        }
      } else {
        if (this.dialogRef !== null) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      }
    });
  }
}
